import React, { useEffect, useState, useCallback } from 'react';
import api, { fmtNum, fmtDate } from '@/lib/api';
import { Loading, SectionTitle } from '@/components/Shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { Plus, Car, FileWarning, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = { vehicle_number: '', vehicle_type: '', tank_capacity: 5000, status: 'available' };

export default function AdminVehicles() {
  const [vehicles, setVehicles] = useState(null);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null); // null = create, id = edit
  const [form, setForm] = useState(EMPTY);
  const load = useCallback(() => api.get('/vehicles').then((r) => setVehicles(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!vehicles) return <Loading />;
  const openCreate = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (v) => { setEditId(v.id); setForm({ vehicle_number: v.vehicle_number, vehicle_type: v.vehicle_type || '', tank_capacity: v.tank_capacity, status: v.status || 'available' }); setOpen(true); };
  const save = async () => {
    try {
      if (editId) { await api.put(`/vehicles/${editId}`, form); toast.success('Vehicle updated'); }
      else { await api.post('/vehicles', form); toast.success('Vehicle added'); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const remove = async (v) => {
    try { await api.delete(`/vehicles/${v.id}`); toast.success(`Vehicle ${v.vehicle_number} deleted`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };
  const docBadge = (st) => st === 'expired' ? 'bg-destructive/10 text-destructive' : st === 'expiring' ? 'bg-amber-500/10 text-amber-600' : 'bg-emerald-500/10 text-emerald-600';
  return (
    <div className="space-y-4">
      <SectionTitle action={<Button size="sm" onClick={openCreate} data-testid="add-vehicle-button"><Plus className="h-4 w-4 mr-1" />Add Vehicle</Button>}>Vehicle Management</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {vehicles.map((v) => (
          <div key={v.id} className="rounded-xl border border-border bg-card p-4" data-testid={`vehicle-card-${v.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Car className="h-5 w-5 text-primary" /><div><p className="font-semibold">{v.vehicle_number}</p><p className="text-xs text-muted-foreground">{v.vehicle_type} · {fmtNum(v.tank_capacity)}L</p></div></div>
              <div className="flex items-center gap-0.5">
                <span className="text-xs rounded-full bg-muted px-2 py-0.5 mr-1">{v.status}</span>
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(v)} data-testid={`edit-vehicle-${v.id}`} aria-label="Edit vehicle"><Pencil className="h-4 w-4" /></Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`delete-vehicle-${v.id}`} aria-label="Delete vehicle"><Trash2 className="h-4 w-4 text-destructive" /></Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Delete vehicle {v.vehicle_number}?</AlertDialogTitle><AlertDialogDescription>This removes the vehicle and its documents. Vehicles on active deliveries cannot be deleted.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(v)} data-testid={`confirm-delete-vehicle-${v.id}`}>Delete</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
            <div className="mt-3 space-y-1">{(v.documents || []).map((d) => <div key={d.id} className="flex items-center justify-between text-xs"><span className="flex items-center gap-1">{d.expiry_status !== 'ok' && <FileWarning className="h-3 w-3" />}{d.doc_type}</span><span className={`rounded-full px-2 py-0.5 ${docBadge(d.expiry_status)}`}>{fmtDate(d.expiry_date)}</span></div>)}</div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>{editId ? 'Edit Vehicle' : 'Add Vehicle'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Vehicle Number</Label><Input value={form.vehicle_number} onChange={(e) => setForm({ ...form, vehicle_number: e.target.value })} data-testid="vehicle-number-input" /></div>
            <div><Label>Type</Label><Input value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })} placeholder="Tanker 8KL" /></div>
            <div><Label>Tank Capacity (L)</Label><Input type="number" value={form.tank_capacity} onChange={(e) => setForm({ ...form, tank_capacity: Number(e.target.value) })} /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-vehicle-button">{editId ? 'Save Changes' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
